// PartnerConsentContext — Cluster F.2 / Q12.5 (just-in-time at first
// partner-service-per-parent).
//
// Mirrors the EntitlementContext (C.3) shape: a single global provider
// owns the modal state; any component calls
//   const allowed = await usePartnerConsentGate().check({partnerId, beneficiaryId});
// and gates its booking submit on the boolean. true means all required
// consent_types are now accepted on the current version; false means
// the user dismissed without accepting (booking must not proceed).
//
// No hidden side effects: the gate ONLY calls GET /api/partner-consents/
// check. The actual accept POST happens inside <PartnerConsentModal>
// after the user checks the boxes and confirms.
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';

// View states for the modal.
export const PARTNER_VIEWS = Object.freeze({
  CLOSED: 'closed',
  LOADING: 'loading',
  PROMPT: 'prompt',       // show the consent form
  SUBMITTING: 'submitting',
  ERROR: 'error',
});

const PartnerConsentContext = createContext(null);


export function PartnerConsentProvider({ children }) {
  const { api } = useAuth();

  const [view, setView] = useState(PARTNER_VIEWS.CLOSED);
  const [checkResult, setCheckResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // Promise resolver — the open `check()` call awaits this so the caller
  // can guard its submit logic on the result.
  const resolverRef = useRef(null);
  const lastInputRef = useRef(null);

  const resolveAndClose = useCallback((allowed) => {
    if (resolverRef.current) {
      resolverRef.current(allowed);
      resolverRef.current = null;
    }
    setView(PARTNER_VIEWS.CLOSED);
    setCheckResult(null);
    setErrorMessage(null);
  }, []);

  // ─── Public: check() returns Promise<boolean> ───────────────────────
  const check = useCallback(
    async ({ partnerId, beneficiaryId = null }) => {
      if (!partnerId) {
        // Defensive: if a caller forgot the partner id, fail-open
        // (allow proceed) so we don't soft-brick a booking page when
        // the partner mapping isn't wired yet.
        return true;
      }

      // Re-entrance guard: if a previous check is still open, dismiss it
      // first so the new one wins. resolveAndClose's pending Promise
      // resolves to false (treated as dismissal).
      if (resolverRef.current) {
        resolveAndClose(false);
      }

      lastInputRef.current = { partnerId, beneficiaryId };
      const decisionPromise = new Promise((resolve) => {
        resolverRef.current = resolve;
      });

      setView(PARTNER_VIEWS.LOADING);
      try {
        const params = { partner_id: partnerId };
        if (beneficiaryId) params.beneficiary_id = beneficiaryId;
        const res = await api.get('/partner-consents/check', { params });
        const data = res.data;
        if (!data.needs_consent) {
          // Already on current version — resolve true without opening.
          resolveAndClose(true);
          return decisionPromise;
        }
        setCheckResult(data);
        setView(PARTNER_VIEWS.PROMPT);
      } catch (err) {
        // 404 / 410 / other → tell the user and resolve false so the
        // booking doesn't proceed silently when consent state is unknown.
        setErrorMessage(
          err.response?.data?.detail ||
          err.message ||
          'Could not load partner consent state.'
        );
        setView(PARTNER_VIEWS.ERROR);
      }
      return decisionPromise;
    },
    [api, resolveAndClose]
  );

  // ─── Modal-facing API ────────────────────────────────────────────────
  // Submit the accept POST. Resolves the gate Promise true on success.
  const submitAccept = useCallback(
    async (consentTypes) => {
      const input = lastInputRef.current;
      const data = checkResult;
      if (!input || !data) return;
      setView(PARTNER_VIEWS.SUBMITTING);
      try {
        const body = {
          partner_id: input.partnerId,
          consent_types: consentTypes,
        };
        if (input.beneficiaryId) body.beneficiary_id = input.beneficiaryId;
        await api.post('/partner-consents/accept', body);
        resolveAndClose(true);
      } catch (err) {
        setErrorMessage(
          err.response?.data?.detail ||
          err.message ||
          'Could not record your consent. Please try again.'
        );
        setView(PARTNER_VIEWS.ERROR);
      }
    },
    [api, checkResult, resolveAndClose]
  );

  // User pressed "Not now" or backdrop — close without accepting.
  const dismiss = useCallback(() => {
    resolveAndClose(false);
  }, [resolveAndClose]);

  // Retry after a transient error.
  const retry = useCallback(async () => {
    const input = lastInputRef.current;
    if (!input) {
      resolveAndClose(false);
      return;
    }
    resolverRef.current = null;
    setErrorMessage(null);
    await check(input);
  }, [check, resolveAndClose]);

  const modalApi = useMemo(
    () => ({
      view,
      checkResult,
      errorMessage,
      submitAccept,
      dismiss,
      retry,
    }),
    [view, checkResult, errorMessage, submitAccept, dismiss, retry]
  );

  const value = useMemo(
    () => ({
      check,
      __modalApi: modalApi,
    }),
    [check, modalApi]
  );

  return (
    <PartnerConsentContext.Provider value={value}>
      {children}
    </PartnerConsentContext.Provider>
  );
}


export function usePartnerConsentGate() {
  const ctx = useContext(PartnerConsentContext);
  if (!ctx) {
    throw new Error('usePartnerConsentGate must be used inside <PartnerConsentProvider>');
  }
  return { check: ctx.check };
}

export function usePartnerConsentModalApi() {
  const ctx = useContext(PartnerConsentContext);
  if (!ctx) {
    throw new Error('usePartnerConsentModalApi must be used inside <PartnerConsentProvider>');
  }
  return ctx.__modalApi;
}
